import React, { useState, useMemo } from "react";
import { X, Phone, Mail, FileText, User, Building, Info, Activity } from "lucide-react";
import { ExternalAgency } from "../../../types/api";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import { S3TempUrl } from "../../../utils/s3Client";
import { toast } from "react-toastify";

interface ViewExternalAgencyProps {
  onClose: () => void;
  agency: ExternalAgency;
}

const ViewExternalAgency: React.FC<ViewExternalAgencyProps> = ({
  onClose,
  agency,
}) => {
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState({ url: "", name: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { getsignedurl } = useMemo(() => S3TempUrl(), []);

  const handleViewDocument = async () => {
    if (!agency.mouContractName) {
      toast.error("No document available");
      return;
    }

    try {
      setIsLoading(true);
      
      const filename = agency.mouContractName.split('/').pop() || "Document";
      const fileType = filename.split('.').pop()?.toLowerCase() || '';
      let filePath = agency.mouContractName;
      
      if (agency.mouContractName.includes('s3.ap-south-1.amazonaws.com/')) {
        const urlParts = agency.mouContractName.split('.s3.ap-south-1.amazonaws.com/');
        if (urlParts.length > 1) {
          filePath = urlParts[1];
        }
      } else if (agency.mouContractName.startsWith('http://') || agency.mouContractName.startsWith('https://')) {
        try {
          const urlObj = new URL(agency.mouContractName);
          filePath = decodeURIComponent(urlObj.pathname.substring(1)); 
        } catch (e) {
          console.error('Error parsing URL:', e);
        }
      }
      const signedUrl = await getsignedurl(filePath, fileType);
      
      if (!signedUrl) {
        throw new Error('Failed to generate signed URL');
      }
      setFileUrl({
        url: signedUrl,
        name: filename,
        type: fileType
      });
      
      setIsDocumentViewerOpen(true);
      
    } catch (error: any) {
      console.error("Error loading document:", error);
      toast.error(error?.message || "Failed to load document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg w-full max-w-4xl mx-auto overflow-hidden">
      {/* Header with gradient background */}
      <div className="flex justify-between items-center p-6 bg-gradient-to-r from-teal-500 to-teal-600 text-white">
        <div className="flex items-center gap-3">
          <Building className="w-8 h-8 opacity-90" />
          <div>
            <h2 className="text-2xl font-bold">{agency.agencyName}</h2>
            <p className="text-teal-100 font-medium opacity-90">
              {agency.category}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Close"
        >
          <X size={24} className="text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Details Section */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
              <User size={20} className="text-teal-600" /> Contact Information
            </h3>
            
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-100 space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                  <User size={18} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact Person</p>
                  <p className="font-medium text-gray-800">{agency.contactPerson}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                 <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone Number</p>
                  <a href={`tel:${agency.contactNumber}`} className="font-medium text-teal-600 hover:text-teal-800 transition-colors">
                    {agency.contactNumber}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Email Address</p>
                  <a href={`mailto:${agency.email}`} className="font-medium text-teal-600 hover:text-teal-800 transition-colors break-all">
                    {agency.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Agency Details Section */}
          <div className="space-y-6">
           <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
              <Info size={20} className="text-teal-600" /> Agency Details
            </h3>

            <div className="bg-gray-50 p-5 rounded-lg border border-gray-100 space-y-4">
               <div className="flex items-start gap-4">
                <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                   <Activity size={18} />
                </div>
                 <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</p>
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mt-1 ${
                      agency.status === "Active"
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : "bg-red-100 text-red-700 border border-red-200"
                    }`}
                  >
                    {agency.status}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-4">
                 <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                  <FileText size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">MOU / Contract</p>
                  {agency.mouContractName ? (
                    <div className="flex items-center gap-2 mt-1 p-2 bg-white border border-gray-200 rounded-md">
                      <FileText size={16} className="text-red-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate flex-1" title={agency.mouContractName}>
                        {agency.mouContractName.split('/').pop() || agency.mouContractName}
                      </span>
                      <button 
                        onClick={handleViewDocument}
                        disabled={isLoading}
                        className="ml-auto text-xs text-teal-600 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 px-2"
                      >
                        {isLoading ? "Loading..." : "View"}
                      </button>
                    </div>
                  ) : (
                     <span className="text-sm text-gray-500 italic">No document uploaded</span>
                  )}
                </div>
              </div>

              {agency.mouContractExpiryDate && (
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-white rounded shadow-sm text-teal-600">
                    <Activity size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">MOU Expiry Date</p>
                    <p className="font-medium text-gray-800">{new Date(agency.mouContractExpiryDate).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
          
      {/* Footer */}
      <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
        <button
          onClick={onClose}
          className="px-6 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-all"
        >
          Close
        </button>
      </div>

      {/* Document Viewer Modal - Same style as ViewFireDrill */}
      {isDocumentViewerOpen && fileUrl.url && (
        <div
          className="fixed inset-0 z-[10000]"
          style={{
            backgroundColor: "rgba(0,0,0,0.7)",
          }}
        >
          <button
            onClick={handleCloseViewer}
            className="absolute right-4 top-4 z-10 bg-red-500 text-white p-2 rounded hover:bg-red-600 transition-colors"
            aria-label="Close viewer"
          >
            <X size={24} />
          </button>
          <div
            className="absolute bg-white border shadow-2xl"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "calc(100vw - 200px)",
              height: "calc(100vh - 100px)",
              overflow: "auto",
              boxSizing: "border-box",
              borderRadius: "8px",
            }}
          >
            {isLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
              </div>
            ) : fileUrl.type === "pdf" ? (
              <PdfViewer fileUrl={fileUrl.url} />
            ) : (
              <DocumentViewer file={fileUrl.url} name={fileUrl.name} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewExternalAgency;